import React, { useState } from 'react';
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  Box,
  IconButton,
  Menu,
  MenuItem,
  Avatar,
  Chip,
  useMediaQuery,
  useTheme,
  Drawer,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  CircularProgress,
} from '@mui/material';
import {
  Menu as MenuIcon,
  Dashboard,
  AccountTree,
  TrackChanges,
  Analytics,
  Person,
  Logout,
  Star,
  AccountCircle,
  Subscriptions,
  Timeline,
  Diamond,
  LightMode,
  DarkMode,
} from '@mui/icons-material';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useSubscription } from '../contexts/SubscriptionContext';
import { useThemeMode } from '../contexts/ThemeContext';

const Navbar: React.FC = () => {
  const { user, logout, loading } = useAuth();
  const { isPremium } = useSubscription();
  const { mode, toggleTheme } = useThemeMode();
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [mobileOpen, setMobileOpen] = useState(false);

  // Temporary debugging
  console.log('Navbar state:', { user: !!user, userName: user?.name, loading });

  const handleMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    logout();
    navigate('/');
    handleClose();
  };

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const navigationItems = [
    { text: 'Dashboard', path: '/dashboard', icon: <Dashboard /> },
    { text: 'Health Tree', path: '/talent-tree', icon: <AccountTree /> },
    { text: 'Health Worth', path: '/health-worth', icon: <Timeline /> },
    { text: 'Lock-In', path: '/weekly-lockin', icon: <TrackChanges /> },
  ];

  const drawer = (
    <Box sx={{ width: 250 }}>
      <Box sx={{ p: 2 }}>
        <Typography variant="h6" sx={{ color: 'primary.main', fontWeight: 'bold' }}>
          Life Tree
        </Typography>
      </Box>
      
      {/* Theme Toggle in Mobile Menu */}
      <Box sx={{ px: 2, pb: 1 }}>
        <Button
          fullWidth
          onClick={toggleTheme}
          startIcon={mode === 'dark' ? <LightMode /> : <DarkMode />}
          sx={{
            justifyContent: 'flex-start',
            color: 'text.primary',
            '&:hover': {
              backgroundColor: 'rgba(139, 92, 246, 0.1)',
            },
          }}
        >
          {mode === 'dark' ? 'Light Mode' : 'Dark Mode'}
        </Button>
      </Box>
      
      <List>
        {navigationItems.map((item) => (
          <ListItem
            key={item.text}
            component={Link}
            to={item.path}
            onClick={() => setMobileOpen(false)}
            sx={{
              color: location.pathname === item.path ? 'primary.main' : 'text.primary',
              backgroundColor: location.pathname === item.path ? 'rgba(139, 92, 246, 0.1)' : 'transparent',
            }}
          >
            <ListItemIcon sx={{ color: 'inherit' }}>
              {item.icon}
            </ListItemIcon>
            <ListItemText primary={item.text} />
          </ListItem>
        ))}
      </List>
    </Box>
  );

  return (
    <>
      <AppBar 
        position="static" 
        sx={{ 
          backgroundColor: mode === 'dark' 
            ? '#0f0f23' 
            : '#ffffff',
          color: mode === 'dark' ? 'inherit' : '#1a202c',
          borderBottom: mode === 'dark' 
            ? '1px solid rgba(255, 255, 255, 0.1)' 
            : '1px solid rgba(0, 0, 0, 0.1)',
          boxShadow: mode === 'dark' 
            ? '0 4px 20px rgba(0, 0, 0, 0.3)' 
            : '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
          position: 'relative',
          zIndex: 1100,
          isolation: 'isolate',
        }}
      >
        <Toolbar>
          {user && isMobile && (
            <IconButton
              color="inherit"
              aria-label="open drawer"
              edge="start"
              onClick={handleDrawerToggle}
              sx={{ mr: 2 }}
            >
              <MenuIcon />
            </IconButton>
          )}

          <Typography
            variant="h6"
            component={Link}
            to="/"
            sx={{
              flexGrow: 1,
              textDecoration: 'none',
              color: 'primary.main',
              fontWeight: 'bold',
              display: 'flex',
              alignItems: 'center',
              gap: 1,
            }}
          >
            <img 
              src="/logo-simple.svg" 
              alt="Life Tree Logo" 
              style={{ 
                width: 28, 
                height: 28,
              }} 
            />
            Life Tree
          </Typography>

          {user && !isMobile && (
            <Box sx={{ display: 'flex', gap: 2, mr: 2 }}>
              {navigationItems.map((item) => (
                <Button
                  key={item.text}
                  component={Link}
                  to={item.path}
                  color="inherit"
                  startIcon={item.icon}
                  sx={{
                    color: location.pathname === item.path ? 'primary.main' : 'text.primary',
                    backgroundColor: location.pathname === item.path ? 'rgba(139, 92, 246, 0.1)' : 'transparent',
                    '&:hover': {
                      backgroundColor: 'rgba(139, 92, 246, 0.1)',
                    },
                  }}
                >
                  {item.text}
                </Button>
              ))}
            </Box>
          )}

          {/* Theme Toggle Button */}
          <IconButton
            onClick={toggleTheme}
            color="inherit"
            sx={{ 
              mr: 1,
              '&:hover': {
                backgroundColor: 'rgba(139, 92, 246, 0.1)',
              },
            }}
            title={`Switch to ${mode === 'dark' ? 'light' : 'dark'} mode`}
          >
            {mode === 'dark' ? <LightMode /> : <DarkMode />}
          </IconButton>

          {user ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {isPremium ? (
                <Chip
                  icon={<Diamond sx={{ color: 'white !important' }} />}
                  label="Premium"
                  size="small"
                  sx={{
                    backgroundColor: 'primary.main',
                    color: 'white',
                    fontWeight: 'bold',
                  }}
                />
              ) : (
                <Button
                  variant="contained"
                  size="small"
                  onClick={() => navigate('/subscription')}
                  sx={{
                    backgroundColor: 'linear-gradient(45deg, #8B5CF6 30%, #A78BFA 90%)',
                    background: 'linear-gradient(45deg, #8B5CF6 30%, #A78BFA 90%)',
                    color: 'white',
                    fontWeight: 'bold',
                    fontSize: '0.75rem',
                    px: 2,
                    py: 0.5,
                    minWidth: 'auto',
                    '&:hover': {
                      background: 'linear-gradient(45deg, #7C3AED 30%, #8B5CF6 90%)',
                    },
                  }}
                >
                  Upgrade to Premium
                </Button>
              )}
              <IconButton
                size="large"
                aria-label="account of current user"
                aria-controls="menu-appbar"
                aria-haspopup="true"
                onClick={handleMenu}
                color="inherit"
              >
                <Avatar sx={{ width: 32, height: 32, backgroundColor: 'primary.main', color: 'white' }}>
                  {user.name.charAt(0).toUpperCase()}
                </Avatar>
              </IconButton>
              <Menu
                id="menu-appbar"
                anchorEl={anchorEl}
                anchorOrigin={{
                  vertical: 'top',
                  horizontal: 'right',
                }}
                keepMounted
                transformOrigin={{
                  vertical: 'top',
                  horizontal: 'right',
                }}
                open={Boolean(anchorEl)}
                onClose={handleClose}
                sx={{
                  '& .MuiPaper-root': {
                    backgroundColor: mode === 'dark' 
                      ? '#1a1a2e' 
                      : '#ffffff',
                    border: mode === 'dark' 
                      ? '1px solid rgba(255, 255, 255, 0.2)' 
                      : '1px solid rgba(0, 0, 0, 0.1)',
                    boxShadow: mode === 'dark' 
                      ? '0 4px 20px rgba(0, 0, 0, 0.5)' 
                      : '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
                  },
                }}
              >
                <MenuItem onClick={() => { navigate('/profile'); handleClose(); }}>
                  <Person sx={{ mr: 1 }} />
                  Profile
                </MenuItem>
                <MenuItem onClick={() => { navigate('/subscription'); handleClose(); }}>
                  <Subscriptions sx={{ mr: 1 }} />
                  {isPremium ? 'Manage Subscription' : 'Upgrade to Premium'}
                </MenuItem>
                <MenuItem onClick={handleLogout}>
                  <Logout sx={{ mr: 1 }} />
                  Logout
                </MenuItem>
              </Menu>
            </Box>
          ) : loading ? (
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <CircularProgress size={24} sx={{ color: 'primary.main' }} />
            </Box>
          ) : (
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button color="inherit" component={Link} to="/login">
                Login
              </Button>
              <Button variant="contained" component={Link} to="/register">
                Sign Up
              </Button>
            </Box>
          )}
        </Toolbar>
      </AppBar>

      {user && (
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true,
          }}
          sx={{
            display: { xs: 'block', md: 'none' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: 250,
              backgroundColor: 'background.default',
              borderRight: mode === 'dark' 
                ? '1px solid rgba(255, 255, 255, 0.1)' 
                : '1px solid rgba(0, 0, 0, 0.1)',
            },
          }}
        >
          {drawer}
        </Drawer>
      )}
    </>
  );
};

export default Navbar; 